import { useCallback, useState } from 'react';
import { DailyVideo, useParticipantProperty } from '@daily-co/daily-react';
import { StopScreenShare as StopScreenShareIcon } from '@mui/icons-material';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { CenteredName, ParticipantBadge, pillBadgeSx } from './VideoBadges';

interface ParticipantTileProps {
  sessionId: string;
  variant: 'main' | 'thumbnail' | 'grid' | 'gridFill';
  isLocal?: boolean;
  isActiveSpeaker?: boolean;
  /** Render DailyVideo with type="screenVideo" */
  isScreenShare?: boolean;
  /**
   * Optional label rendered as an overlay (top-left), e.g. "Your share". Used by
   * the local screen-share preview to make it unambiguous which track is shown.
   */
  label?: string;
  /**
   * Optional click handler. When set, the tile renders as an interactive button
   * with a hover affordance — used to stop the local screen share with one click.
   */
  onClick?: () => void;
  /** Accessible label for the clickable tile (required when onClick is set). */
  clickAriaLabel?: string;
  /**
   * Tooltip-style hover hint shown alongside the icon when the tile is interactive.
   * Used by the local screen-share preview ("Click to stop sharing").
   */
  hoverHint?: string;
  /** Highlight border, e.g. for the local screen-share preview. */
  highlightBorder?: boolean;
  /** Suppress the participant badge (mic + name). Useful for the local-screen tile. */
  hideParticipantBadge?: boolean;
}

const variantSx = {
  main: { width: '100%', height: '100%' },
  thumbnail: { width: '100%', aspectRatio: '16/9' },
  grid: { width: '100%', aspectRatio: '16/9' },
  gridFill: { width: '100%', height: '100%' },
} as const;

export function ParticipantTile({
  sessionId,
  variant,
  isLocal = false,
  isActiveSpeaker = false,
  isScreenShare = false,
  label,
  onClick,
  clickAriaLabel,
  hoverHint,
  highlightBorder = false,
  hideParticipantBadge = false,
}: ParticipantTileProps) {
  const userName = useParticipantProperty(sessionId, 'user_name');
  const video = useParticipantProperty(sessionId, 'video');

  const [videoAR, setVideoAR] = useState<number | null>(null);

  const handleResize = useCallback(({ aspectRatio }: { aspectRatio: number }) => {
    setVideoAR(aspectRatio);
  }, []);

  const objectFit = isScreenShare
    ? 'contain'
    : videoAR !== null && videoAR >= 1
      ? 'cover'
      : 'contain';

  const displayName = isLocal ? (userName ? `${userName} (You)` : 'You') : userName || 'Guest';
  const showVideo = isScreenShare || video;

  const wrapperSx = {
    position: 'relative' as const,
    bgcolor: 'grey.800',
    borderRadius: 2,
    overflow: 'hidden' as const,
    ...(isActiveSpeaker
      ? { outlineOffset: -2, outline: '2px solid', outlineColor: 'primary.main' }
      : {}),
    ...(highlightBorder
      ? { outlineOffset: -2, outline: '2px solid', outlineColor: 'info.light' }
      : {}),
    ...variantSx[variant],
  };

  const inner = (
    <>
      {showVideo ? (
        <DailyVideo
          sessionId={sessionId}
          type={isScreenShare ? 'screenVideo' : 'video'}
          mirror={isLocal && !isScreenShare}
          onResize={handleResize}
          style={{
            width: '100%',
            height: '100%',
            objectFit,
          }}
        />
      ) : (
        <CenteredName name={displayName} />
      )}

      {label && (
        // Mirror ParticipantBadge styling (pill, fontSize 11, alignItems center) so the
        // "Your share" overlay sits in the same visual family as the participant name badge.
        <Stack
          direction="row"
          sx={[
            { alignItems: 'center', pointerEvents: 'none' },
            pillBadgeSx,
            { top: 4, bottom: 'auto' },
          ]}
        >
          <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1 }}>
            {label}
          </Typography>
        </Stack>
      )}

      {!hideParticipantBadge && (
        <ParticipantBadge sessionId={sessionId} isLocal={isLocal} />
      )}

      {onClick && hoverHint && (
        <Box
          className="participant-tile__hover-hint"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 0.5,
            bgcolor: 'rgba(0,0,0,0.45)',
            color: 'common.white',
            opacity: 0,
            transition: 'opacity 120ms ease',
            pointerEvents: 'none',
          }}
        >
          <StopScreenShareIcon sx={{ fontSize: 24 }} />
          <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
            {hoverHint}
          </Typography>
        </Box>
      )}
    </>
  );

  if (onClick) {
    // Wrapper Box owns the layout (aspectRatio, dimensions) so the clickable variant
    // renders at exactly the same size as the non-clickable Box variant. ButtonBase's
    // default `display: inline-flex` interacts oddly with `aspect-ratio` in some flex
    // containers, which previously made the screen-share tile shorter than its
    // sibling camera tiles in the SpeakerView sidebar.
    return (
      <Box sx={wrapperSx}>
        <ButtonBase
          onClick={onClick}
          aria-label={clickAriaLabel ?? label ?? 'Stop screen share'}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            display: 'block',
            textAlign: 'inherit',
            cursor: 'pointer',
            '&:hover .participant-tile__hover-hint, &:focus-visible .participant-tile__hover-hint':
              {
                opacity: 1,
              },
          }}
        >
          {inner}
        </ButtonBase>
      </Box>
    );
  }

  return <Box sx={wrapperSx}>{inner}</Box>;
}
